import React, { useEffect, useState } from "react";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import {
  ADD_COURSE_LABEL,
  ATTENDANCE_YEAR_LABEL,
  CANCEL_LABEL,
  CONFIRM_LABEL,
  COURSE_LABEL,
  COURSE_NAME_LABEL,
  EDIT_COURSE_LABEL,
  IS_NOT_VALID_LABEL,
  IS_REQUIRED_LABEL,
  OTHER_LABEL,
} from "../../../../../../../constants/Dictionary";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../icons/close.svg";
import {
  Select,
  SelectOption,
  SelectVariantEnum,
} from "../../../../../../../components/core/Select/Select";
import TextInput from "../../../../../../../components/core/TextInput/TextInput";
import NumberInput from "../../../../../../../components/core/NumberInput/NumberInput";
import { FileUploader } from "../../../../../../../components/core/UploadFile/FileUploader";
import { FileUploaderSizeEnum } from "../../../../../../../types/AppTypes";
import {
  AddCollaboratorsHistoryCourse,
  CollaboratorDetails,
  CollaboratorHistoryCourse,
  UpdateCollaboratorsHistoryCourse,
} from "../../../../../../../types/api/collaborators.type";
import * as yup from "yup";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import { StyledHistoryCourseModal } from "./HistoryCourseModal.styled";
import { useDebounce } from "use-debounce";
import { checkHasMoreData } from "../../../../../../../utils/pagination.utils";
import { TextBox } from "../../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import {
  useCourseHistoryAdd,
  useCourseHistoryCertificate,
  useCourseHistoryUpdate,
  useCourseList,
} from "../../../../../../../hooks/formations.hook";

type HistoryCourseModalProps = {
  course?: CollaboratorHistoryCourse | null;
  isOpen: boolean;
  onSave: () => void;
  onClose: () => void;
  collaborator: CollaboratorDetails;
  certificateId?: number;
};

type FormData = {
  courseId?: number | null;
  name?: string;
  year: number | null;
  certificateFile: {
    file?: File | null;
    name?: string | null;
  } | null;
};

export const HistoryCourseModal = ({
  course,
  isOpen,
  onSave,
  onClose,
  collaborator,
  certificateId,
}: HistoryCourseModalProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [pageNumber, setPageNumber] = useState(0);
  const [courseOptions, setCourseOptions] = useState<SelectOption<number>[]>(
    [],
  );

  const profileId = collaborator.id;

  const { data: courseData, isLoading: isLoadingCourse } = useCourseList({
    profileId,
    pageNumber,
    searchDebouncedValue,
  });
  const { data: certificate } = useCourseHistoryCertificate({
    profileId,
    isOpen,
    certificateId,
  });
  const { mutateAsync: updateCourses, isPending: pendingUpdateCourses } =
    useCourseHistoryUpdate(profileId);
  const { mutateAsync: addCourse, isPending: pendingAddCourse } =
    useCourseHistoryAdd(profileId);

  useEffect(() => {
    if (!courseData) return;
    const newOptions = courseData.data.map((course) => ({
      value: course.id,
      label: course.name,
    }));
    if (pageNumber === 0) {
      setCourseOptions([{ value: -1, label: OTHER_LABEL }, ...newOptions]);
    } else {
      setCourseOptions((prev) => [...prev, ...newOptions]);
    }
    setHasMore(checkHasMoreData(courseData.pagination));
  }, [courseData]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue]);

  const schema = (isNewCourse: boolean) =>
    yup.object({
      courseId: yup
        .number()
        .nullable()
        .when([], {
          is: () => isNewCourse,
          then: (field) =>
            field.required(`${COURSE_LABEL} ${IS_REQUIRED_LABEL}`),
          otherwise: (field) => field.notRequired(),
        }),
      name: yup.string().when(["courseId"], {
        is: (courseId: number) => courseId === -1 || !isNewCourse,
        then: (field) =>
          field.required(`${COURSE_NAME_LABEL} ${IS_REQUIRED_LABEL}`),
        otherwise: (field) => field.notRequired(),
      }),
      year: yup
        .number()
        .nullable()
        .min(1970, `${ATTENDANCE_YEAR_LABEL} ${IS_NOT_VALID_LABEL}`)
        .max(
          new Date().getFullYear(),
          `${ATTENDANCE_YEAR_LABEL} ${IS_NOT_VALID_LABEL}`,
        )
        .required(`${ATTENDANCE_YEAR_LABEL} ${IS_REQUIRED_LABEL}`),
      certificateFile: yup
        .object({
          file: yup.mixed<File>().nullable(),
          name: yup.string().nullable(),
        })
        .nullable(),
    });

  const {
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema(!course)) as Resolver<FormData>,
  });

  const watchCourse = watch("courseId");

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      if (course) {
        const updateBody: UpdateCollaboratorsHistoryCourse = {
          courseName: data.name,
          attendanceYear: data.year!,
        };

        await updateCourses({
          courseId: course.id,
          updateBody,
          data,
        });
      } else {
        const addBody: AddCollaboratorsHistoryCourse = {
          courseId:
            data.courseId !== undefined
              ? data.courseId === -1
                ? null
                : data.courseId
              : null,
          courseName: data.courseId === -1 ? data.name! : null!,
          external: data.courseId === -1 ? true : false,
          attendanceYear: data.year!,
        };
        await addCourse({ addBody, data });
      }
    } finally {
      closeModal();
      onSave();
    }
  };

  const closeModal = () => {
    onClose();
  };

  useEffect(() => {
    if (course) {
      reset({
        courseId: null,
        name: course.course.name,
        year: course.attendanceYear,
        certificateFile: {
          name: course.fileName,
        },
      });
    } else {
      reset({
        courseId: null,
        name: "",
        year: null,
        certificateFile: null,
      });
    }
  }, [course, isOpen]);

  const canEditName = (course && course.external) || watchCourse === -1;
  const isReadOnly = course?.fromCmSheet === true;

  const pendingModal = pendingUpdateCourses || pendingAddCourse;

  return (
    <Modal
      fullSize={false}
      label={course ? EDIT_COURSE_LABEL : ADD_COURSE_LABEL}
      isOpen={isOpen}
      leftButton={
        <Button
          label={CANCEL_LABEL}
          variant={ButtonVariants.SECONDARY}
          icon={<CloseIcon />}
          onClick={closeModal}
          disabled={pendingModal}
        />
      }
      rightButton={
        <Button
          label={CONFIRM_LABEL}
          variant={ButtonVariants.PRIMARY}
          icon={<CheckIcon />}
          disabled={pendingModal || !isDirty}
          type="submit"
          form="historyCourseForm"
        />
      }
    >
      <StyledHistoryCourseModal>
        <Loader loading={pendingModal}>
          <form id="historyCourseForm" onSubmit={handleSubmit(onSubmit)}>
            <div className="input-row">
              {course && !course.external && (
                <div className="mb">
                  <TextBox
                    label={COURSE_NAME_LABEL}
                    text={course.course.name}
                  />
                </div>
              )}
              {!course && (
                <FormField error={errors.courseId?.message}>
                  <Controller
                    name="courseId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.DEFAULT}
                        label={COURSE_LABEL}
                        options={courseOptions}
                        {...field}
                        onSearch={setSearchValue}
                        searchValue={searchValue}
                        loading={isLoadingCourse || courseOptions.length === 0}
                        canLoadMore={hasMore}
                        fetchMore={() => setPageNumber((p) => p + 1)}
                        withSearch
                      />
                    )}
                  />
                </FormField>
              )}
              {canEditName && (
                <FormField error={errors.name?.message}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={COURSE_NAME_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              )}
            </div>
            <FormField error={errors.year?.message}>
              <div className="input-row">
                {isReadOnly ? (
                  <TextBox
                    label={ATTENDANCE_YEAR_LABEL}
                    text={String(course?.attendanceYear ?? "")}
                  />
                ) : (
                  <Controller
                    name="year"
                    control={control}
                    render={({ field }) => (
                      <NumberInput label={ATTENDANCE_YEAR_LABEL} {...field} />
                    )}
                  />
                )}
              </div>
            </FormField>
            <FormField error={errors.certificateFile?.message}>
              <Controller
                name="certificateFile"
                control={control}
                render={({ field }) => {
                  return (
                    <FileUploader
                      size={FileUploaderSizeEnum.REGULAR}
                      onFileUpload={(file: File) => {
                        field.onChange({ file: file, name: file.name });
                      }}
                      value={field.value?.file || null}
                      fileName={field.value?.name ?? ""}
                      fileSize={
                        field.value?.file
                          ? field.value.file.size
                          : certificate
                            ? certificate.byteLength
                            : undefined
                      }
                      handleDeleteFile={() => {
                        field.onChange(null);
                      }}
                    />
                  );
                }}
              />
            </FormField>
          </form>
        </Loader>
      </StyledHistoryCourseModal>
    </Modal>
  );
};
